import type {
    EmployeePickerElement,
    RequiredFieldElement,
    RequiredFieldForm,
    SettingListElement,
} from 'types/back-office-settings';
import { REQUIRED_FIELD_CARDS, rowField } from './requiredFieldLayout';
import type { SettingRowLayout, SettingSectionLayout } from './settingLayout';

export interface ShownRow {
    layout: SettingRowLayout;
    setting: SettingListElement;
}

export interface ShownSection {
    layout: SettingSectionLayout;
    groups: { id: string; rows: ShownRow[] }[];
    /** The section's settings and how many are on, whatever the filter hides. Required fields are not settings. */
    total: number;
    on: number;
    /** The entries of the Required fields card the section draws under its settings; none without a card. */
    requiredFields: RequiredFieldElement[];
    /** The rows of the "Listed in employee pickers" card, which the Colleagues section draws under its settings. */
    employeePickers: EmployeePickerElement[];
}

/** The section the "Listed in employee pickers" card sits in. */
const EMPLOYEE_PICKERS_ANCHOR = 'colleagues';

/**
 * The sections the page draws. A row is drawn when the backend declares its setting and the paid module it lives in,
 * if any, is on for the viewer. A group with no row drawn is left out, and so is a section with neither a row nor a
 * card under its settings (Required fields, employee pickers).
 */
export function shownSections(
    sections: SettingSectionLayout[],
    settings: SettingListElement[],
    enabledFeatures: readonly string[],
    requiredFields: RequiredFieldElement[],
    employeePickers: EmployeePickerElement[] = []
): ShownSection[] {
    const byKey = new Map(settings.map((setting) => [setting.key, setting]));
    return sections
        .map((layout) => {
            const groups = layout.groups
                .map((group) => ({
                    id: group.id,
                    rows: group.rows.flatMap((row) => {
                        const setting = byKey.get(row.key);
                        const shown = setting && (!setting.paidFlag || enabledFeatures.includes(setting.paidFlag));
                        return shown ? [{ layout: row, setting }] : [];
                    }),
                }))
                .filter((group) => group.rows.length > 0);
            const rows = groups.flatMap((group) => group.rows);
            const form = REQUIRED_FIELD_CARDS[layout.anchor];
            return {
                layout,
                groups,
                total: rows.length,
                on: rows.filter((row) => row.setting.enabled).length,
                requiredFields: form ? requiredFields.filter((entry) => entry.form === form) : [],
                employeePickers: layout.anchor === EMPLOYEE_PICKERS_ANCHOR ? employeePickers : [],
            };
        })
        .filter(
            (section) => section.total > 0 || section.requiredFields.length > 0 || section.employeePickers.length > 0
        );
}

/**
 * The rows whose label or description holds the filter, case-insensitive, and the Required fields rows whose field
 * name does, or all of a card whose title does; a group, card or section left with none is left out. A blank filter
 * keeps everything.
 */
export function filterSections(
    sections: ShownSection[],
    filter: string,
    textOf: (key: string) => string,
    /** A card's title without a field, else the name of the field's row. */
    requiredFieldText: (form: RequiredFieldForm, field?: string) => string
): ShownSection[] {
    const needle = filter.trim().toLocaleLowerCase();
    if (!needle) return sections;
    const holds = (text: string) => text.toLocaleLowerCase().includes(needle);
    return sections
        .map((section) => ({
            ...section,
            groups: section.groups
                .map((group) => ({ ...group, rows: group.rows.filter((row) => holds(textOf(row.layout.key))) }))
                .filter((group) => group.rows.length > 0),
            requiredFields: section.requiredFields.filter(
                (entry) => holds(requiredFieldText(entry.form)) || holds(requiredFieldText(entry.form, rowField(entry)))
            ),
            // The filter does not search the employee pickers card, so it leaves it out.
            employeePickers: [],
        }))
        .filter((section) => section.groups.length > 0 || section.requiredFields.length > 0);
}
